<template>
  <section class="profile-screen">
    <div class="profile-shell">
      <button class="profile-back" @click="$emit('close')">返回首页</button>

      <div v-if="view === 'profile'" class="profile-layout">
        <aside class="profile-summary">
          <span class="eyebrow">User Center</span>
          <h2>个人中心设置</h2>
          <p>管理你的探索者名称，或进入独立的密码安全界面。</p>
          <div class="profile-badge">
            <strong>{{ profileForm.name || displayName || '探索者' }}</strong>
            <span>{{ profileForm.username || 'Backrooms Operator' }}</span>
          </div>
        </aside>

        <section class="profile-panel">
          <div class="block-head">
            <span class="eyebrow">Profile</span>
            <strong>基础资料</strong>
          </div>
          <el-form :model="profileForm" label-position="top" class="profile-form">
            <el-form-item label="账号" v-if="profileForm.username">
              <el-input v-model="profileForm.username" disabled />
            </el-form-item>
            <el-form-item label="名称">
              <el-input v-model="profileForm.name" maxlength="10" placeholder="设置展示名称" show-word-limit />
            </el-form-item>
            <div class="profile-actions">
              <el-button type="primary" :loading="profileSaving" @click="saveProfile">保存名称</el-button>
              <el-button class="profile-link-btn" @click="view = 'password'">修改密码</el-button>
            </div>
          </el-form>
        </section>
      </div>

      <div v-else class="profile-layout password-layout">
        <aside class="profile-summary">
          <span class="eyebrow">Security</span>
          <h2>修改密码</h2>
          <p>这是独立的密码安全界面。完成修改后，可返回个人中心继续设置资料。</p>
        </aside>

        <section class="profile-panel">
          <div class="block-head">
            <span class="eyebrow">Password</span>
            <strong>密码安全</strong>
          </div>
          <el-form :model="passwordForm" label-position="top" class="profile-form">
            <el-form-item label="旧密码">
              <el-input v-model="passwordForm.old" type="password" maxlength="20" show-password />
            </el-form-item>
            <el-form-item label="新密码">
              <el-input v-model="passwordForm.new" type="password" maxlength="20" show-password />
            </el-form-item>
            <el-form-item label="确认新密码">
              <el-input v-model="passwordForm.new1" type="password" maxlength="20" show-password />
            </el-form-item>
            <div class="profile-actions">
              <el-button type="primary" :loading="passwordSaving" @click="savePassword">保存密码</el-button>
              <el-button class="profile-link-btn" @click="view = 'profile'">返回个人中心</el-button>
            </div>
          </el-form>
        </section>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { ajax } from '@/api';
import { userData } from '@/store/userData';

defineProps<{
  displayName: string;
}>();

defineEmits<{
  (e: 'close'): void;
}>();

const useData = userData();
const view = ref<'profile' | 'password'>('profile');
const profileSaving = ref(false);
const passwordSaving = ref(false);
const profileForm = reactive({
  username: '',
  name: ''
});
const passwordForm = reactive({
  old: '',
  new: '',
  new1: ''
});

const loadProfile = () => {
  profileForm.name = useData.name || sessionStorage.getItem('name') || '';
  ajax({
    url: 'user/findMe',
    method: 'POST',
    isLoading: false,
    success: (res: any) => {
      if (res.code !== 0) return;
      const data = res.data || {};
      profileForm.username = data.username || '';
      profileForm.name = data.name || profileForm.name;
    }
  });
};

const saveProfile = () => {
  const name = profileForm.name.trim();
  if (!name) {
    ElMessage.warning('请输入名称');
    return;
  }
  profileSaving.value = true;
  ajax({
    url: 'user/user-info/update',
    method: 'POST',
    data: { name },
    success: (res: any) => {
      profileSaving.value = false;
      if (res.code === 0) {
        useData.setName(name);
        ElMessage.success(res.msg || '名称已更新');
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      profileSaving.value = false;
      ElMessage.error('保存失败，请稍后重试');
    }
  });
};

const savePassword = () => {
  if (!passwordForm.old || !passwordForm.new || !passwordForm.new1) {
    ElMessage.warning('请完整填写密码');
    return;
  }
  if (passwordForm.new.length < 8 || passwordForm.new.length > 20) {
    ElMessage.warning('新密码长度必须为8-20位');
    return;
  }
  if (passwordForm.new !== passwordForm.new1) {
    ElMessage.error('两次输入的密码不一致');
    return;
  }
  passwordSaving.value = true;
  ajax({
    url: 'user/password/update',
    method: 'POST',
    data: passwordForm,
    success: (res: any) => {
      passwordSaving.value = false;
      if (res.code === 0) {
        passwordForm.old = '';
        passwordForm.new = '';
        passwordForm.new1 = '';
        ElMessage.success(res.msg || '密码已更新');
        view.value = 'profile';
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      passwordSaving.value = false;
      ElMessage.error('密码修改失败，请稍后重试');
    }
  });
};

onMounted(loadProfile);
</script>

<style scoped lang="scss">
.profile-screen {
  max-width: 1320px;
  margin: 0 auto;
}

.profile-shell {
  min-height: calc(100vh - 120px);
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    var(--surface);
  border-radius: 8px;
  padding: 24px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.38),
    0 0 26px rgba(0, 240, 216, 0.055),
    inset 0 1px 0 rgba(0, 240, 216, 0.1);
  backdrop-filter: blur(26px) saturate(125%);
}

.profile-back {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  margin-bottom: 22px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.42);
  background: rgba(0, 240, 216, 0.09);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.11);
}

.profile-back::before {
  content: "<";
  margin-right: 8px;
  font-weight: 700;
}

.profile-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 520px);
  gap: 20px;
  align-items: stretch;
}

.password-layout {
  grid-template-columns: minmax(0, 0.82fr) minmax(360px, 500px);
}

.profile-summary,
.profile-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    rgba(8, 13, 26, 0.68);
  padding: 24px;
}

.profile-summary {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 420px;
}

.profile-summary h2 {
  max-width: 720px;
  margin-top: 14px;
  font-size: clamp(38px, 5vw, 68px);
  line-height: 0.98;
}

.profile-summary p {
  max-width: 560px;
  margin-top: 18px;
  color: var(--muted);
  line-height: 1.8;
}

.profile-badge {
  margin-top: 44px;
  border-left: 2px solid var(--accent);
  background: rgba(0, 240, 216, 0.06);
  padding: 16px 18px;
  border-radius: 0 8px 8px 0;
}

.profile-badge strong,
.profile-badge span {
  display: block;
}

.profile-badge strong {
  color: var(--text);
  font-size: 24px;
}

.profile-badge span {
  margin-top: 8px;
  color: var(--muted);
}

.profile-panel {
  align-self: start;
}

.block-head {
  margin-bottom: 16px;
}

.block-head strong {
  display: block;
  color: var(--text);
}

.profile-form {
  margin-top: 18px;
}

.profile-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.profile-link-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
}

:deep(.profile-form .el-form-item__label) {
  color: var(--muted);
}

:deep(.profile-form .el-input__wrapper) {
  border-radius: 8px;
  background: rgba(4, 9, 20, 0.86) !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.24) inset !important;
}

:deep(.profile-form .el-input__inner) {
  color: var(--text) !important;
}

:deep(.profile-form .el-input__count),
:deep(.profile-form .el-input__count-inner) {
  color: #fff !important;
  background: transparent !important;
  box-shadow: none !important;
}

@media (max-width: 980px) {
  .profile-layout,
  .password-layout {
    grid-template-columns: 1fr;
  }

  .profile-summary {
    min-height: auto;
  }

  .profile-panel {
    align-self: stretch;
  }
}
</style>
